import { Component, HostListener, Input, OnInit, Output, EventEmitter, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Guid } from 'guid-typescript';
import { firstValueFrom } from 'rxjs';
import { JobModel } from 'src/app/models/job.model';
import { SkillModel } from 'src/app/models/skill.model';
import { SupplierSkillsModel } from 'src/app/models/supplier-skills.model';
import { SupplierModel } from 'src/app/models/supplier.model';
import { TaskGruopModel } from 'src/app/models/task-group.model';
import { SupplierService } from 'src/app/services/supplier.services';
import { ToastService } from 'src/app/services/toast.service';
import { WorkTaskService } from 'src/app/services/work-task.service';
import { UserService } from 'src/app/shared/services/user.service';
import { NgClass, KeyValuePipe } from '@angular/common';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSelect, MatOption } from '@angular/material/select';
import { FilterByTaskGroupPipe } from '../../../shared/pipes/filter-by-taskGroup';
import { escapeBelongsToOverlay } from '../escape-key';

@Component({
    selector: 'app-supplier-skills',
    templateUrl: './supplier-skills.component.html',
    styleUrls: ['./supplier-skills.component.scss'],
    imports: [NgClass, FormsModule, ReactiveFormsModule, MatSlideToggle, MatSelect, MatOption, KeyValuePipe, FilterByTaskGroupPipe]
})
export class SupplierSkillsComponent implements OnInit {
  private readonly supplierService = inject(SupplierService);
  activatedRoute = inject(ActivatedRoute);
  private readonly workTaskSvc = inject(WorkTaskService);
  toastr = inject(ToastService);
  usrSvc = inject(UserService);


  taskGroups: TaskGruopModel[] = [];
  jobs: JobModel[] = [];
  @Input() supplier!: SupplierModel;
  skillsForm = new UntypedFormGroup({});
  supplierId: string = '';
  supplierSkills!: SupplierSkillsModel;
  groupedSupplierSkills: Map<string, string[]> = new Map<string, string[]>();
  hasChanges: boolean = true;
  bothSkills: any[] = ['carga', 'entrega'];
  showSkillForm: boolean = false;
  @Output() moveToSectionTrigger = new EventEmitter<void>();

  ngOnInit(): void {
    this.supplierId = this.supplier.id.toString();
    this.getTaskGroups();
  }

  getTaskGroups() {
    this.workTaskSvc.getTaskGroups().subscribe((data: TaskGruopModel[]) => {
      this.taskGroups = data;
      data.forEach((d) => {
        if (d.description.toLowerCase() != 'transporte') {
          this.skillsForm.addControl(d.id.toString(), new UntypedFormControl());
        } else {
          this.skillsForm.addControl(
            d.id.toString(),
            new UntypedFormControl(false)
          );
        }
      });
      this.getSkills();
    });
  }

  async getJobs(groupId: Guid): Promise<JobModel[]> {
    try {
      const data: JobModel[] = await firstValueFrom(
        this.workTaskSvc.getJobs(groupId)
      );
      data.forEach((j) => {
        j.groupId = groupId;
      });
      this.jobs = [...this.jobs, ...data];
      return data;
    } catch (error) {
      console.error('Error fetching jobs:', error);
      return [];
    }
  }

  groupBySkills(data: SkillModel[]) {
    const groupBy = (array: any[], key: string) => {
      return array.reduce((result, currentValue) => {
        result[currentValue[key]] = result[currentValue[key]] || [];
        result[currentValue[key]].push(currentValue);

        return result;
      }, {});
    };

    return groupBy(data, 'categoryId');
  }

  getSkills() {
    if (this.supplierId) {
      this.supplierService.getSkills(this.supplierId).subscribe((data) => {
        this.handleSupplierSkillsData(data);
      });
    }
  }

  handleSupplierSkillsData(data: SkillModel[]) {
    this.supplierSkills = { id: this.supplierId, skills: data };
    this.groupSupplierSkills();
    const uniqueJobIds = new Set<any>();
    this.jobs = [];
    let groups: any = [];
    Object.entries(this.groupBySkills(data)).forEach((g: any) => {
      groups.push(g[0]);

      this.workTaskSvc.getJobs(g[0]).subscribe((data: JobModel[]) => {
        data.forEach((j) => {
          j.groupId = g[0];
          if (!uniqueJobIds.has(j.id)) {
            uniqueJobIds.add(j);
            this.jobs.push(j);
          }
        });

        this.setSavedSkills(g[1], g[0]);
      });
    });
    this.getMissingJobs(groups);
  }

  groupSupplierSkills() {
    const groupedSkillsTemp = new Map<string, string[]>();
    this.supplierSkills.skills.forEach((item) => {
      const key = item.categoryName;
      if (groupedSkillsTemp.has(key)) {
        groupedSkillsTemp.get(key)!.push(item.subCategoryName);
      } else {
        groupedSkillsTemp.set(key, [item.subCategoryName]);
      }
    });

    this.groupedSupplierSkills = groupedSkillsTemp;
    this.moveToSectionTrigger.emit();
  }

  async onSubmit() {
    this.disableClick();
    let values: JobModel[] = [];

    for (const group of this.taskGroups) {
      const value = this.skillsForm.controls[group.id.toString()].value;
      if (group.description.toLowerCase() != 'transporte') {
        if (value) values.push(...(Array.isArray(value) ? value : [value]));
      } else if (value) {
        const transportJobs = await this.getJobs(group.id);
        values = [...values, ...transportJobs];
      }
    }

    this.saveSkills(values);
  }

  saveSkills(values: JobModel[]) {
    let skills = values.map((j) => {
      return <SkillModel>{
        id: Guid.createEmpty(),
        categoryId: j.groupId,
        subCategoryId: j.id,
      };
    });

    this.supplierService.saveSkills(this.supplierId, skills).subscribe(
      (data) => {
        this.toastr.success(
          'Actualización',
          'Se han actualizado las aptitudes del prestador correctamente.'
        );
        this.getTaskGroups();
        this.closeSkillForm();
      },
      (err) => {
        this.toastr.error(
          'Error',
          'Se produjo un error y no se han podido actualizar todas las aptitudes del prestador.'
        );
      }
    );
  }

  pachControlValue(val: any, groupId: any, undivisibleSkills: boolean = false) {
    this.hasChanges = false;
    if (undivisibleSkills) {
      this.skillsForm.controls[groupId].patchValue(val.checked);
    } else {
      this.skillsForm.controls[groupId].patchValue(val);
    }
  }

  setSavedSkills(skills: SkillModel[], groupId: any) {
    let resultArray: any = [];

    skills.forEach((model: SkillModel) => {
      resultArray.push(
        this.jobs.find(
          (x) => x.id == model.subCategoryId && x.groupId == model.categoryId
        )
      );
    });
    this.skillsForm.controls[groupId].patchValue(resultArray);
    resultArray = [];
  }

  getMissingJobs(groups: any[]) {
    let notInList = this.taskGroups.filter((x) => !groups.includes(x.id));

    notInList.forEach((g) => {
      void this.getJobsForGroup(g.id);
    });
  }

  private async getJobsForGroup(id: any): Promise<void> {
    await this.getJobs(id);
  }

  // deprecated
  removeSkill(model: JobModel, groupId: string) {
    let currentList = this.skillsForm.controls[groupId].value as JobModel[];
    if (
      currentList.some((skill) =>
        this.bothSkills.includes(skill.description.toLowerCase())
      )
    ) {
      this.pachControlValue([], groupId, true);
    } else {
      let newList = currentList.filter((x) => x.id != model.id);
      this.pachControlValue(newList, groupId);
    }
  }

  disableClick() {
    this.hasChanges = true;
  }

  openSkillsForm() {
    this.showSkillForm = true;
  }

  @HostListener('window:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent) {
    // Escape on an open Subcategorías dropdown closes only the dropdown.
    if (this.showSkillForm && !escapeBelongsToOverlay(event)) {
      this.closeSkillForm();
    }
  }

  closeSkillForm() {
    this.disableClick();
    this.showSkillForm = false;
  }
}
